/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useState } from "react";
import { MagnifyingGlassPlusIcon } from "@heroicons/react/24/outline";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { UI_SCALES, getUiScale, setUiScale } from "@/utils/uiScale";
import { cn } from "@/lib/utils";

export const InterfaceSettings: React.FC = () => {
  const [scale, setScale] = useState(getUiScale);
  const pick = (s: number) => {
    setUiScale(s);
    setScale(s);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white sm:text-2xl">
          <MagnifyingGlassPlusIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
          Interface scale
        </h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">Make everything in the panel smaller or larger. It applies to this browser only.</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Size</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Interface scale">
            {UI_SCALES.map((s) => (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={scale === s}
                onClick={() => pick(s)}
                className={cn(
                  "min-w-[64px] rounded-lg border px-3 py-2 text-sm font-medium tabular-nums transition-colors",
                  scale === s
                    ? "border-emerald-500/60 bg-emerald-500/10 text-gray-900 dark:text-white"
                    : "border-gray-200 text-gray-600 hover:bg-gray-100 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5",
                )}
              >
                {`${Math.round(s * 100)}%`}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">100% is the default. Use a larger size on a wall display or a distant screen, a smaller one to fit more on a laptop.</p>
        </CardContent>
      </Card>
    </div>
  );
};
